/**
 * Premium custom sector illustrations.
 * Each icon is a single-stroke SVG, designed monochrome for the
 * royal-green cube faces. ViewBox 100×100 — scales to any size.
 */

const Base = ({ children, className = "" }) => (
  <svg
    viewBox="0 0 100 100"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    {children}
  </svg>
);

// ---------- 1. AI — neural network brain ----------
export const AIIcon = (props) => (
  <Base {...props}>
    {/* Outer brain contour */}
    <path
      d="M30 32 C22 36, 22 50, 28 54 C24 62, 32 72, 42 70 C46 78, 58 78, 62 70 C72 72, 78 62, 74 54 C80 50, 80 36, 72 32 C70 22, 58 20, 52 24 C46 20, 34 22, 30 32 Z"
      stroke="currentColor"
      strokeOpacity="0.85"
    />
    {/* Inner connections */}
    <line x1="40" y1="38" x2="50" y2="50" stroke="currentColor" />
    <line x1="60" y1="38" x2="50" y2="50" stroke="currentColor" />
    <line x1="40" y1="62" x2="50" y2="50" stroke="currentColor" />
    <line x1="60" y1="62" x2="50" y2="50" stroke="currentColor" />
    <line x1="35" y1="50" x2="50" y2="50" stroke="currentColor" />
    <line x1="65" y1="50" x2="50" y2="50" stroke="currentColor" />
    {/* Nodes */}
    {[
      [40, 38], [60, 38], [40, 62], [60, 62],
      [35, 50], [65, 50],
    ].map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r="2.2" fill="currentColor" />
    ))}
    <circle cx="50" cy="50" r="3.6" fill="currentColor" />
    <circle cx="50" cy="50" r="6" stroke="currentColor" strokeOpacity="0.6" />
  </Base>
);

// ---------- 2. Web3 — interconnected hex nodes ----------
export const Web3Icon = (props) => {
  const hex = (cx, cy, r = 8) => {
    const pts = Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 3) * i - Math.PI / 6;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(" ");
    return pts;
  };
  const nodes = [
    [50, 22],
    [26, 36],
    [74, 36],
    [26, 64],
    [74, 64],
    [50, 78],
    [50, 50],
  ];
  return (
    <Base {...props}>
      {/* Connecting lines from center */}
      {nodes.slice(0, 6).map(([x, y], i) => (
        <line key={i} x1="50" y1="50" x2={x} y2={y} stroke="currentColor" strokeOpacity="0.7" />
      ))}
      {/* Outer hexagons */}
      {nodes.slice(0, 6).map(([x, y], i) => (
        <polygon
          key={`h${i}`}
          points={hex(x, y, 7)}
          stroke="currentColor"
          strokeOpacity="0.85"
        />
      ))}
      {/* Center hex */}
      <polygon
        points={hex(50, 50, 9)}
        fill="rgba(19,98,7,0.2)"
        stroke="currentColor"
      />
      <circle cx="50" cy="50" r="2.5" fill="currentColor" />
    </Base>
  );
};

// ---------- 3. Space — rocket + orbit ----------
export const SpaceIcon = (props) => (
  <Base {...props}>
    {/* Orbit ellipse */}
    <ellipse
      cx="50"
      cy="55"
      rx="34"
      ry="12"
      stroke="currentColor"
      strokeOpacity="0.5"
      transform="rotate(-20 50 55)"
    />
    {/* Planet on orbit */}
    <circle cx="22" cy="60" r="3" fill="currentColor" />
    {/* Rocket body */}
    <path
      d="M50 18 L58 36 L58 62 L50 70 L42 62 L42 36 Z"
      stroke="currentColor"
      strokeOpacity="0.9"
    />
    {/* Nose tip highlight */}
    <line x1="50" y1="18" x2="50" y2="38" stroke="currentColor" />
    {/* Window */}
    <circle cx="50" cy="40" r="3.5" stroke="currentColor" fill="rgba(19,98,7,0.25)" />
    {/* Fins */}
    <path d="M42 56 L34 66 L42 62 Z" stroke="currentColor" strokeOpacity="0.9" />
    <path d="M58 56 L66 66 L58 62 Z" stroke="currentColor" strokeOpacity="0.9" />
    {/* Flame */}
    <path d="M46 70 L50 82 L54 70" stroke="currentColor" />
    {/* Stars */}
    <g stroke="currentColor" strokeOpacity="0.7">
      <line x1="20" y1="24" x2="20" y2="30" />
      <line x1="17" y1="27" x2="23" y2="27" />
      <line x1="80" y1="20" x2="80" y2="24" />
      <line x1="78" y1="22" x2="82" y2="22" />
      <line x1="78" y1="74" x2="78" y2="78" />
      <line x1="76" y1="76" x2="80" y2="76" />
    </g>
  </Base>
);

// ---------- 4. Agritech — leaf with circuit ----------
export const AgritechIcon = (props) => (
  <Base {...props}>
    {/* Leaf shape */}
    <path
      d="M22 70 C22 40, 50 22, 78 22 C78 50, 60 78, 30 78 C24 78, 22 76, 22 70 Z"
      stroke="currentColor"
      strokeOpacity="0.9"
    />
    {/* Central vein */}
    <path d="M22 70 L78 22" stroke="currentColor" />
    {/* Side veins */}
    <path d="M40 64 L50 50" stroke="currentColor" strokeOpacity="0.7" />
    <path d="M52 70 L60 56" stroke="currentColor" strokeOpacity="0.7" />
    <path d="M30 56 L40 44" stroke="currentColor" strokeOpacity="0.7" />
    <path d="M62 38 L72 30" stroke="currentColor" strokeOpacity="0.7" />
    {/* Circuit nodes */}
    <circle cx="40" cy="64" r="2" fill="currentColor" />
    <circle cx="52" cy="70" r="2" fill="currentColor" />
    <circle cx="30" cy="56" r="2" fill="currentColor" />
    <circle cx="62" cy="38" r="2" fill="currentColor" />
    <circle cx="78" cy="22" r="2.5" fill="currentColor" />
    {/* Stem */}
    <line x1="22" y1="70" x2="14" y2="86" stroke="currentColor" strokeOpacity="0.9" />
  </Base>
);

// ---------- 5. Energy — sun + lightning ----------
export const EnergyIcon = (props) => (
  <Base {...props}>
    {/* Sun rays */}
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (Math.PI / 6) * i;
      const x1 = 50 + Math.cos(a) * 26;
      const y1 = 50 + Math.sin(a) * 26;
      const x2 = 50 + Math.cos(a) * 36;
      const y2 = 50 + Math.sin(a) * 36;
      return (
        <line
          key={i}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={i % 2 === 0 ? "#ffffff" : "#136207"}
          strokeOpacity={i % 2 === 0 ? 0.85 : 0.9}
        />
      );
    })}
    {/* Sun core */}
    <circle cx="50" cy="50" r="22" stroke="currentColor" strokeOpacity="0.9" />
    <circle cx="50" cy="50" r="14" stroke="currentColor" strokeOpacity="0.4" />
    {/* Lightning bolt */}
    <path
      d="M52 36 L42 52 L50 52 L46 64 L58 48 L50 48 L54 36 Z"
      fill="rgba(19,98,7,0.3)"
      stroke="currentColor"
    />
  </Base>
);

// ---------- 6. Fintech — card + chip + graph ----------
export const FintechIcon = (props) => (
  <Base {...props}>
    {/* Card */}
    <rect
      x="18"
      y="32"
      width="64"
      height="40"
      rx="4"
      stroke="currentColor"
      strokeOpacity="0.9"
    />
    {/* Stripe */}
    <line x1="18" y1="42" x2="82" y2="42" stroke="currentColor" strokeOpacity="0.6" />
    {/* Chip */}
    <rect
      x="26"
      y="50"
      width="14"
      height="11"
      rx="1.5"
      fill="rgba(19,98,7,0.3)"
      stroke="currentColor"
    />
    <line x1="33" y1="50" x2="33" y2="61" stroke="currentColor" />
    <line x1="26" y1="55.5" x2="40" y2="55.5" stroke="currentColor" />
    {/* Mini graph bars */}
    <line x1="52" y1="62" x2="52" y2="56" stroke="currentColor" strokeWidth="2" />
    <line x1="58" y1="62" x2="58" y2="52" stroke="currentColor" strokeWidth="2" />
    <line x1="64" y1="62" x2="64" y2="48" stroke="currentColor" strokeWidth="2" />
    <line x1="70" y1="62" x2="70" y2="54" stroke="currentColor" strokeWidth="2" />
    {/* Arrow */}
    <path
      d="M50 24 L58 18 L66 22"
      stroke="currentColor"
    />
  </Base>
);

// ---------- 7. Infrastructure — smart city skyline ----------
export const InfrastructureIcon = (props) => (
  <Base {...props}>
    {/* Buildings */}
    <rect x="20" y="46" width="14" height="34" stroke="currentColor" strokeOpacity="0.9" />
    <rect x="36" y="34" width="16" height="46" stroke="currentColor" strokeOpacity="0.9" />
    <rect x="54" y="50" width="12" height="30" stroke="currentColor" strokeOpacity="0.9" />
    <rect x="68" y="40" width="14" height="40" stroke="currentColor" strokeOpacity="0.9" />
    {/* Window glints */}
    {[
      [24, 52], [24, 58], [24, 64], [24, 70],
      [40, 40], [40, 46], [40, 52], [40, 58], [40, 64], [40, 70],
      [44, 40], [44, 46], [44, 52], [44, 58], [44, 64], [44, 70],
      [58, 56], [58, 62], [58, 68], [58, 74],
      [72, 46], [72, 52], [72, 58], [72, 64], [72, 70],
      [76, 46], [76, 52], [76, 58], [76, 64], [76, 70],
    ].map(([x, y], i) => (
      <rect key={i} x={x} y={y} width="2" height="2" fill="currentColor" />
    ))}
    {/* Wireless signals on tallest */}
    <path d="M44 30 Q44 26 48 26" stroke="currentColor" />
    <path d="M44 28 Q44 22 50 22" stroke="currentColor" strokeOpacity="0.7" />
    <circle cx="44" cy="32" r="1.5" fill="currentColor" />
    {/* Ground line */}
    <line x1="14" y1="80" x2="86" y2="80" stroke="currentColor" strokeOpacity="0.6" />
    {/* Connection nodes */}
    <circle cx="14" cy="84" r="1.5" fill="currentColor" />
    <circle cx="50" cy="84" r="1.5" fill="currentColor" />
    <circle cx="86" cy="84" r="1.5" fill="currentColor" />
  </Base>
);

export const SECTOR_ICONS = {
  AI: AIIcon,
  Web3: Web3Icon,
  Space: SpaceIcon,
  Agritech: AgritechIcon,
  Energy: EnergyIcon,
  Fintech: FintechIcon,
  Infrastructure: InfrastructureIcon,
};
